import React, { useCallback, useMemo } from 'react';
import { Box, Hint, HintText, ShimmerBox, Text } from '@common-fe/common-fe';

import { AccentText } from '@/components';
import Link from '@/modules/core/components/Link';
import { useCurrentOrganization } from '@/modules/core/hooks';
import currentTheme from '@/styles/theme';

import { StatisticCard, StatisticDetailsIcon, SweepAngleGlobalStyle } from '../../StatisticCard.styles';
import { useFraudBorderSweepAnimation } from '../hooks';

import { StyledTitle } from './FraudReviewStatistic.styles';

export interface FraudReviewStatisticProps {
  isLoading?: boolean;
  count: number;
  url: string;
  isDetailsLinkVisible?: boolean;
}

export const FraudReviewStatistic: React.FC<FraudReviewStatisticProps> = ({
  isLoading,
  count,
  url,
  isDetailsLinkVisible,
}) => {
  const hasIssues = useMemo(() => count > 0, [count]);

  const {
    observingOrganization: { id: organizationId },
  } = useCurrentOrganization();

  const { cardRef, isAnimating } = useFraudBorderSweepAnimation({
    hasCount: hasIssues,
    isLoading,
    organizationId,
  });

  const background = useMemo(() => {
    if (hasIssues) {
      return `${currentTheme.colors.dangerContainer}80`;
    }
    return 'cavas';
  }, [hasIssues]);

  const getWrapper = useCallback(
    (node: React.ReactNode) => {
      if (isDetailsLinkVisible) {
        return (
          <Link testId="dashboard_statistics_fraud-review_details-link" to={url}>
            {node}
          </Link>
        );
      }
      return node;
    },
    [isDetailsLinkVisible, url]
  );

  return getWrapper(
    <>
      {/*
        Rendered as a sibling (not a card child): createGlobalStyle emits no visible DOM, but as a
        flex child of the column-layout card it still consumed a `gap` slot and grew the card's
        height while animating. Kept outside the flex flow so the card height never changes.
      */}
      {isAnimating && <SweepAngleGlobalStyle />}
      <StatisticCard
        ref={cardRef}
        data-testid="dashboard_statistics_fraud-review"
        background="canvas"
        border={{ color: 'border2', size: '1px' }}
        round="container1Round"
        pad="spacing16"
        direction="column"
        gap="spacing12"
        height={{ min: '140px' }}
        $isAnimating={isAnimating}
      >
        <Box direction="row" align="center" justify="between" gap="spacing12" height="20px">
          <Hint
            hintWidth={320}
            hintHeight={140}
            hintElement={
              <Box>
                <HintText>
                  <StyledTitle
                    data-testid="dashboard_statistics_fraud-review_title"
                    size="medium"
                    weight={700}
                  >
                    Fraud Review Required
                  </StyledTitle>
                </HintText>
              </Box>
            }
          >
            <Text data-testid="dashboard_statistics_fraud-review_title_hint" textAlign="center" size="small">
              Number of employees with claim activity identified as potentially suspicious. Review and confirm
              the activity is legitimate or flag it as potential fraud.
            </Text>
          </Hint>

          {isDetailsLinkVisible && (
            <Box justify="center" align="center" width="24px" height="24px">
              <StatisticDetailsIcon
                data-testid="dashboard_statistics_fraud-review_details-icon"
                color="iconDisabled"
              />
            </Box>
          )}
        </Box>
        <Box
          data-testid="dashboard_statistics_fraud-review_value"
          background={background}
          round="container2Round"
          border={{ color: hasIssues ? 'canvas' : 'border2', size: '1px' }}
          pad={{ horizontal: 'spacing12', vertical: 'spacing8' }}
          direction="column"
          justify="center"
          gap="spacing2"
        >
          {isLoading ? (
            <>
              <ShimmerBox
                data-testid="dashboard_statistics_fraud-review_loader"
                round="checkboxRound"
                width="77px"
                height="36px"
              />
              <ShimmerBox round="checkboxRound" width="100%" height="20px" margin={{ top: 'spacing2' }} />
            </>
          ) : (
            <>
              <AccentText
                data-testid="dashboard_statistics_fraud-review_count"
                size="2xl"
                weight={700}
                color="textBody"
              >
                {count}
              </AccentText>
              <Text
                data-testid="dashboard_statistics_fraud-review_label"
                size="medium"
                weight={400}
                color="textBody"
              >
                {count === 1 ? 'Employee' : 'Employees'}
              </Text>
            </>
          )}
        </Box>
      </StatisticCard>
    </>
  );
};

export default FraudReviewStatistic;
